import { useGetFormattedDate } from '@app/src/components/DateDisplay'
import { sustainabilityAreaTranslationKeys } from '@app/src/pages/TopicalRisk/CompanyPage/TopicalRiskAreaCard'
import type Assessment from '@app/src/types/assessment'
import type { BenchmarkGroupType } from '@app/src/types/benchmark'
import { Box, Button, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import type { KpiBar } from '../benchmark/kpiCardLogic'
import {
  baselineComparison,
  baselineStatusMessage,
  disclosureBadge,
  disclosureBar,
  resolveKpiCardState,
} from '../benchmark/kpiCardLogic'
import type { BaselineBenchmark } from '../benchmark/types'
import type { BaselineAreaRow } from '../profileLogic'
import { baselineLevel } from '../profileLogic'
import DeltaValue from './DeltaValue'
import KpiBadgeChip from './KpiBadgeChip'
import KpiComparisonText, { useBenchmarkMeanLine } from './KpiComparisonText'
import PeerMeanMarker from './PeerMeanMarker'
import { LEVEL_TONE_COLOR } from './profileColors'

interface BaselinePerformanceSectionProps {
  baseline: Assessment
  peerBaseline?: BaselineBenchmark
  peerGroup: BenchmarkGroupType
  areaRows: BaselineAreaRow[]
  link?: string
}

const BAR_HEIGHT = 8
const AREA_COLUMN_WIDTH = 180

const LevelSegments: React.FC<{ bar: KpiBar; levelsCount: number; markerLabel?: string }> = ({
  bar,
  levelsCount,
  markerLabel,
}) => (
  <Box position="relative">
    <Stack direction="row" height={BAR_HEIGHT} gap={0.5} borderRadius={999} overflow="hidden">
      {Array.from({ length: levelsCount }, (_, index) => (
        <Box key={index} flex={1} bgcolor={index < bar.fill ? LEVEL_TONE_COLOR[bar.tone] : 'grey.200'} />
      ))}
    </Stack>
    {bar.marker !== undefined && <PeerMeanMarker percent={(100 * bar.marker) / levelsCount} label={markerLabel} />}
  </Box>
)

const LevelRatio: React.FC<{ level?: number; levelsCount: number }> = ({ level, levelsCount }) => {
  const { formatNumber } = useIntl()
  if (level === undefined)
    return (
      <Typography variant="subtitle1" color="text.disabled">
        —
      </Typography>
    )
  return (
    <Typography variant="subtitle1">
      {formatNumber(level, { maximumFractionDigits: 1 })}
      <Typography component="span" variant="inherit" color="text.disabled">
        /{levelsCount}
      </Typography>
    </Typography>
  )
}

const AreaColumns: React.FC<{ first: React.ReactNode; rest: React.ReactNode[]; isHeader?: boolean }> = ({
  first,
  rest,
  isHeader = false,
}) => (
  <Stack role="row" direction="row" alignItems="center" spacing={2} width="100%">
    <Box role={isHeader ? 'columnheader' : 'rowheader'} width={AREA_COLUMN_WIDTH} flexShrink={0}>
      {first}
    </Box>
    {rest.map((cell, index) => (
      <Box key={index} role={isHeader ? 'columnheader' : 'cell'} flex={1} minWidth={0}>
        {cell}
      </Box>
    ))}
  </Stack>
)

const AreaTable: React.FC<{ rows: BaselineAreaRow[] }> = ({ rows }) => {
  const { formatMessage } = useIntl()
  const header = (id: string) => (
    <Typography variant="caption" color="text.primary">
      {formatMessage({ id })}
    </Typography>
  )

  return (
    <Stack
      role="table"
      aria-label={formatMessage({ id: 'sustainabilityProfile.baseline.title' })}
      flex={1}
      minWidth={0}
      bgcolor="grey.50"
      border={1}
      borderColor="divider"
      borderRadius={2}
    >
      <Box role="rowgroup" px={2} py={1.5}>
        <AreaColumns
          isHeader
          first={header('sustainabilityProfile.baseline.columns.area')}
          rest={[
            header('sustainabilityProfile.topics.columns.score'),
            header('sustainabilityProfile.topics.columns.peers'),
            header('sustainabilityProfile.topics.columns.vsPeers'),
          ]}
        />
      </Box>
      <Box role="rowgroup" border={1} borderColor="divider" borderRadius={2} overflow="hidden" mx="-1px" mb="-1px">
        {rows.map(row => (
          <Stack
            key={row.area}
            direction="row"
            alignItems="center"
            height={56}
            px={2}
            bgcolor="background.paper"
            sx={{ '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}
          >
            <AreaColumns
              first={
                <Typography variant="subtitle1">
                  {formatMessage({ id: sustainabilityAreaTranslationKeys[row.area] })}
                </Typography>
              }
              rest={[
                <LevelRatio key="score" level={row.level} levelsCount={row.levelsCount} />,
                <LevelRatio key="peers" level={row.peerLevel} levelsCount={row.levelsCount} />,
                <DeltaValue key="delta" delta={row.delta} positiveColor="secondary.main" />,
              ]}
            />
          </Stack>
        ))}
      </Box>
    </Stack>
  )
}

const BaselinePerformanceSection: React.FC<BaselinePerformanceSectionProps> = ({
  baseline,
  peerBaseline,
  peerGroup,
  areaRows,
  link,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const { formatValueToDate } = useGetFormattedDate()
  const assessed = baselineLevel(baseline)

  const state = resolveKpiCardState(assessed?.level, baselineComparison(assessed?.level, peerBaseline))
  const meanLine = useBenchmarkMeanLine(peerGroup, state.kind === 'compared' ? state.comparison : undefined)
  if (!assessed) return null

  const badge = disclosureBadge(state)
  const bar = disclosureBar(state)

  return (
    <Stack spacing={3}>
      <Typography variant="subtitle1">{formatMessage({ id: 'sustainabilityProfile.baseline.title' })}</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} p={2} bgcolor="background.paper" borderRadius={2}>
        <Stack flex={1} minWidth={0} p={2} spacing={2}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} flex={1}>
            <Typography variant="kpi" component="p">
              {formatMessage(
                { id: 'sustainabilityProfile.baseline.level' },
                {
                  level: formatNumber(assessed.level, { maximumFractionDigits: 1 }),
                  levels: assessed.levelsCount,
                  muted: (chunks: React.ReactNode) => (
                    <Typography component="span" variant="inherit" color="text.disabled">
                      {chunks}
                    </Typography>
                  ),
                },
              )}
            </Typography>
            {badge && <KpiBadgeChip badge={badge} />}
          </Stack>

          <KpiComparisonText meanLine={meanLine} status={baselineStatusMessage(state)} />

          {bar && <LevelSegments bar={bar} levelsCount={assessed.levelsCount} markerLabel={meanLine} />}

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
            <Typography variant="caption">
              {formatMessage(
                { id: 'sustainabilityProfile.baseline.sourceAndDate' },
                {
                  source: formatMessage({
                    id: baseline.isScreened ? 'assessment.alert.aiScreened' : 'assessment.alert.selfReported',
                  }),
                  date: formatValueToDate({ value: baseline.updatedAt ?? baseline.createdAt }, { month: 'short' }),
                },
              )}
            </Typography>
            {link && (
              <Button component={RouterLink} to={link} size="large">
                {formatMessage({ id: 'general.view' })}
              </Button>
            )}
          </Stack>
        </Stack>

        {areaRows.length > 0 && <AreaTable rows={areaRows} />}
      </Stack>
    </Stack>
  )
}

export default BaselinePerformanceSection
